import { useSupportPhone } from '../lib/brand';
import {
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  CircleCheck,
  CircleSlash,
  FileWarning,
  MapPinOff,
  Pause,
  Phone,
  Play,
  Power,
  ShieldAlert,
  ShieldCheck,
  TriangleAlert,
  Volume2,
  Wallet,
  X,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ACCESS, Notice, PRESENCE } from '@despachador/ui';
import { useDriver } from '../store';
import { canInstall, enablePush, install, isNativeApp, isStandalone, onInstallable, openAppSettings, pushState, type PushState } from '../lib/device';
import type { TripResult } from '../types';
import { Trip } from './Trip';

/**
 * The working screen. It is always the answer to "what is happening and what do I do now":
 * the trip while there is one, otherwise the driver's availability.
 */
export function Home() {
  const { me, active } = useDriver();
  if (!me) return null;
  return active ? <Trip service={active} /> : <Standby />;
}

type Presence = 'ONLINE' | 'PAUSED' | 'OFFLINE';

/** Something that stops offers (danger) or will soon (warn): one row of «Qué te impide recibir servicios». */
interface Problem {
  key: string;
  tone: 'danger' | 'warn';
  icon: ReactNode;
  title: string;
  text: ReactNode;
  action?: ReactNode;
  /** stops offers now (a warning only limits them or will) */
  blocks: boolean;
}

/** A requirement of the checklist shown while disconnected: Cuota · Documentos · Ubicación. */
interface Check {
  name: string;
  value: string;
  tone: 'ok' | 'warn' | 'danger';
  to?: string;
}

function Standby() {
  const supportPhone = useSupportPhone();
  const { me, presence, setPresence, geo, audioReady, enableAudio, result, dismissResult } = useDriver();
  const [push, setPush] = useState<PushState | null>(null);
  const [installable, setInstallable] = useState(canInstall());
  const [busy, setBusy] = useState(false);
  useEffect(() => onInstallable(setInstallable), []);
  // Android app: connecting asks for the notification permission, so the answer is read again
  useEffect(() => void pushState().then(setPush), [presence]);
  if (!me) return null;
  const d = me.dashboard;
  const access = d.access;
  const compliance = me.profile.compliance;
  const docsOk = compliance.driver.valid && (!compliance.vehicle || compliance.vehicle.valid);
  // required documents in their warning window, with no renewal sent yet
  const expiring = [...(me.profile.requirements?.driver ?? []), ...(me.profile.requirements?.vehicle ?? [])].filter((r) => r.required && r.state === 'EXPIRING' && !r.pendingDocumentId);
  const suspended = me.profile.status === 'SUSPENDED';
  const working = presence !== 'OFFLINE' && !suspended;
  // suspended by the company (screen 08): its reason as written and whom to call; requirements and radio do not apply
  if (suspended)
    return (
      <div className="standby">
        <section className="status-card blocked" aria-labelledby="state-title">
          <h1 id="state-title" className="micro-title">
            Tu estado
          </h1>
          <div className="state" aria-live="polite">
            <span className="state-dot" aria-hidden />
            Cuenta suspendida
          </div>
          <p className="state-hint">No puedes conectarte mientras siga así.</p>
          {me.profile.suspension_reason && <p className="suspension-reason">«{me.profile.suspension_reason}»</p>}
        </section>
        {supportPhone && (
          <div className="action-bar">
            <a className="btn primary lg" href={`tel:${supportPhone.replace(/[^+\d]/g, '')}`}>
              <Phone size={20} aria-hidden /> Llamar a la central
            </a>
          </div>
        )}
      </div>
    );
  const go = async (s: Presence) => {
    setBusy(true);
    await setPresence(s);
    setBusy(false);
  };

  // everything that stops or limits offers, most serious first (same order and words as before DRV-010)
  const docsLink = (
    <Link to="/cuenta/documentos" className="btn sm">
      Ver documentos
    </Link>
  );
  const quotaLink = (
    <Link to="/cuenta/cuota" className="btn sm">
      Ver mi cuota
    </Link>
  );
  const problems: Problem[] = [];
  if (suspended) problems.push({ key: 'suspended', tone: 'danger', icon: <ShieldAlert size={20} aria-hidden />, title: 'Cuenta suspendida', text: me.profile.suspension_reason, blocks: true });
  if (!access.allowsDispatch)
    problems.push({
      key: 'quota',
      tone: 'danger',
      icon: <Wallet size={20} aria-hidden />,
      title: `Cuota: ${ACCESS[access.status]?.label ?? access.status}`,
      text: `${access.reason}. No recibirás servicios hasta regularizar tu pago.`,
      action: quotaLink,
      blocks: true,
    });
  if (working && geo === 'denied')
    problems.push({
      key: 'geo',
      tone: 'danger',
      icon: <MapPinOff size={20} aria-hidden />,
      title: 'Ubicación bloqueada',
      text: `${isNativeApp ? 'Permite la ubicación «Siempre» o «Mientras se usa» para Central' : 'Activa el permiso de ubicación del navegador para esta app'}: sin ubicación no se te asignan servicios.`,
      action: isNativeApp ? (
        <button type="button" className="btn sm" onClick={() => void openAppSettings()}>
          Abrir ajustes
        </button>
      ) : undefined,
      blocks: true,
    });
  if (!docsOk)
    problems.push({ key: 'docs', tone: 'warn', icon: <FileWarning size={20} aria-hidden />, title: 'Documentos pendientes o vencidos', text: 'No recibirás servicios hasta que estén vigentes.', action: docsLink, blocks: true });
  if (docsOk && expiring.length > 0)
    problems.push({
      key: 'expiring',
      tone: 'warn',
      icon: <CalendarClock size={20} aria-hidden />,
      title: expiring.length === 1 ? `${expiring[0]!.documentTypeName} vence pronto` : `${expiring.length} documentos vencen pronto`,
      text: 'Renuévalos antes de la fecha para seguir recibiendo servicios.',
      action: docsLink,
      blocks: false,
    });
  if (access.allowsDispatch && access.status === 'GRACE_PERIOD')
    problems.push({ key: 'grace', tone: 'warn', icon: <Wallet size={20} aria-hidden />, title: 'Tu cuota venció', text: 'Estás en periodo de gracia: sigues recibiendo servicios por ahora.', action: quotaLink, blocks: false });
  if (working && geo === 'unavailable')
    problems.push({ key: 'gps', tone: 'warn', icon: <MapPinOff size={20} aria-hidden />, title: 'No se pudo obtener tu ubicación', text: 'Revisa que el GPS esté encendido y que tengas señal. Seguiremos intentando.', blocks: false });

  // anything here means "connected, but no trip will reach you": said next to the state, not below it
  const blocked = problems.some((p) => p.blocks) || (working && geo === 'denied');
  const hint: Record<string, ReactNode> = {
    OFFLINE: 'No estás recibiendo servicios.',
    ONLINE: blocked ? (
      <>
        <TriangleAlert size={20} aria-hidden /> Estás conectado, pero no te llegarán servicios. Revisa los avisos.
      </>
    ) : (
      'Esperando servicios. Deja la app abierta y la pantalla encendida.'
    ),
    PAUSED: 'En pausa no recibes ofertas.',
    BUSY: 'Tienes un servicio asignado. Cargando…',
    SUSPENDED: 'Tu cuenta está suspendida: no puedes conectarte.',
  };

  // the checklist while disconnected: the three things a driver needs to receive services
  const checks: Check[] = [
    {
      name: 'Cuota',
      value: ACCESS[access.status]?.label ?? access.status,
      tone: !access.allowsDispatch ? 'danger' : access.status === 'GRACE_PERIOD' ? 'warn' : 'ok',
      to: access.allowsDispatch && access.status === 'ACTIVE' ? undefined : '/cuenta/cuota',
    },
    {
      name: 'Documentos',
      value: !docsOk ? 'Revisar' : expiring.length ? 'Vencen pronto' : 'Vigentes',
      tone: !docsOk ? 'danger' : expiring.length ? 'warn' : 'ok',
      to: !docsOk || expiring.length ? '/cuenta/documentos' : undefined,
    },
    {
      name: 'Ubicación',
      value: geo === 'denied' ? 'Bloqueada' : geo === 'unavailable' ? 'Sin señal' : 'Permitida',
      tone: geo === 'denied' ? 'danger' : geo === 'unavailable' ? 'warn' : 'ok',
    },
  ];
  const showProblems = presence !== 'OFFLINE' && problems.length > 0;
  // while disconnected, what stops offers keeps «Conectarme» off and says why below it
  // only what the central enforces (quota, documents): the location permission is asked when connecting
  const offlineBlocked = presence === 'OFFLINE' && checks.some((c) => c.tone === 'danger' && c.name !== 'Ubicación');
  const allOk = checks.every((c) => c.tone === 'ok');
  const allBlocking = problems.some((p) => p.blocks);

  return (
    <div className="standby">
      {result && <ResultCard result={result} onDismiss={dismissResult} />}

      <section className={`status-card ${presence}${blocked && presence === 'ONLINE' ? ' blocked' : ''}`} aria-labelledby="state-title">
        <h1 id="state-title" className="micro-title">
          Tu estado
        </h1>
        <div className="state" aria-live="polite">
          <span className="state-dot" aria-hidden>
            {presence === 'PAUSED' && <Pause size={10} strokeWidth={3} />}
          </span>
          {PRESENCE[presence]?.label ?? presence}
        </div>
        <p className="state-hint">{hint[presence]}</p>
        {(presence === 'ONLINE' || presence === 'PAUSED') && (
          <div className="state-actions">
            {presence === 'ONLINE' && (
              <button type="button" className="btn" disabled={busy} onClick={() => void go('PAUSED')}>
                <Pause size={18} aria-hidden /> Pausar
              </button>
            )}
            <button type="button" className="btn disconnect" disabled={busy} onClick={() => void go('OFFLINE')}>
              <Power size={18} aria-hidden /> Desconectarme
            </button>
          </div>
        )}
      </section>

      {/* beside the state on a wide screen, under it on a phone */}
      <div className="standby-side">
        {working && !audioReady && (
          <button type="button" className="sound-row" onClick={() => void enableAudio()}>
            <Volume2 size={20} aria-hidden /> Toca para activar el sonido de las alertas
          </button>
        )}

        {presence === 'OFFLINE' && allOk && (
          <p className="all-good">
            <ShieldCheck size={20} aria-hidden /> Cuota, documentos y ubicación · Al día
          </p>
        )}

        {presence === 'OFFLINE' && !allOk && (
          <section aria-labelledby="ready-title">
            <h2 id="ready-title" className="micro-title">
              {offlineBlocked ? 'Qué te impide recibir servicios' : 'Para tener en cuenta'}
            </h2>
            <ul className="check-list">
              {checks.map((c) => {
                const body = (
                  <>
                    {c.tone === 'ok' ? <CircleCheck size={20} aria-hidden className="ico" /> : <TriangleAlert size={20} aria-hidden className="ico" />}
                    <span className="check-name">{c.name}</span>
                    <span className="check-value">{c.value}</span>
                    {c.to && <ChevronRight size={18} aria-hidden className="chev" />}
                  </>
                );
                return (
                  <li key={c.name} className={c.tone}>
                    {c.to ? (
                      <Link to={c.to} className="check-row">
                        {body}
                      </Link>
                    ) : (
                      <span className="check-row">{body}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {presence !== 'OFFLINE' && problems.length === 0 && (
          <p className="all-good">
            <ShieldCheck size={20} aria-hidden /> Cuota, documentos y ubicación al día.
          </p>
        )}

        {showProblems && (
          <section aria-labelledby="problems-title">
            <h2 id="problems-title" className="micro-title">
              {allBlocking ? 'Qué te impide recibir servicios' : 'Para tener en cuenta'}
            </h2>
            <ul className="problem-list">
              {problems.map((p) => (
                <li key={p.key} className={p.tone} role={p.tone === 'danger' ? 'alert' : undefined}>
                  <span className="ico">{p.icon}</span>
                  <div className="problem-body">
                    <b>{p.title}</b>
                    {p.text && <p>{p.text}</p>}
                    {p.action}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* what does not stop offers stays a notice under the rest */}
        <div className="stack tight">
          {push === 'disabled' && (
            <Notice
              tone="warn"
              title="Notificaciones desactivadas"
              action={
                <button type="button" className="btn sm primary" onClick={() => void enablePush(me.pushPublicKey, me.pushNative).then(setPush)}>
                  Activar notificaciones
                </button>
              }
            >
              Actívalas para enterarte de servicios con la app en segundo plano.
            </Notice>
          )}
          {isNativeApp && push === 'denied' && (
            <Notice
              tone="warn"
              title="Notificaciones bloqueadas"
              action={
                <button type="button" className="btn sm" onClick={() => void openAppSettings()}>
                  Abrir ajustes
                </button>
              }
            >
              Permite las notificaciones de Central: sin ellas no verás los servicios con la pantalla apagada o en otra app.
            </Notice>
          )}
          {installable && !isStandalone() && (
            <Notice
              title="Instala la app"
              action={
                <button type="button" className="btn sm" onClick={() => void install()}>
                  Instalar app
                </button>
              }
            >
              Queda en tu pantalla de inicio y abre más rápido.
            </Notice>
          )}
        </div>

        {/* the day in one line, without money: the customer may be looking at this screen. Amounts live in Viajes */}
        <Link to="/viajes" className="today" aria-label={`Hoy: ${d.completed_today} servicios completados. Ver viajes y ganancias`}>
          <span className="micro-title">Hoy</span>
          <span className="today-count">
            {d.completed_today ? (
              <>
                <b>{d.completed_today}</b> {d.completed_today === 1 ? 'servicio completado' : 'servicios completados'}
              </>
            ) : (
              'Aún no has completado servicios'
            )}
          </span>
          <ChevronRight size={18} aria-hidden className="chev" />
        </Link>
      </div>

      {/* the one action of the screen, fixed at the bottom where the trip steps also go (screen 08) */}
      {presence === 'OFFLINE' && (
        <div className="action-bar">
          <button type="button" className="btn primary lg go-online" disabled={busy || suspended || offlineBlocked} onClick={() => void go('ONLINE')}>
            <Power size={22} aria-hidden /> {busy ? 'Conectando…' : 'Conectarme'}
          </button>
          {offlineBlocked && !suspended && <p className="action-note">Podrás conectarte cuando todo esté al día.</p>}
        </div>
      )}
      {presence === 'PAUSED' && (
        <div className="action-bar">
          <button type="button" className="btn primary lg go-online" disabled={busy} onClick={() => void go('ONLINE')}>
            <Play size={22} aria-hidden /> Reanudar
          </button>
        </div>
      )}
    </div>
  );
}

const RESULT: Record<TripResult['kind'], { title: string; tone: 'ok' | 'warn'; text: string }> = {
  completed: { title: 'Servicio finalizado', tone: 'ok', text: 'Ya está en tus viajes.' },
  cancelled: { title: 'Servicio cancelado', tone: 'warn', text: 'Quedó registrado con tu motivo.' },
  no_show: { title: 'Cliente no se presentó', tone: 'warn', text: 'Quedó reportado a la central.' },
  released: { title: 'El servicio ya no es tuyo', tone: 'warn', text: 'La central lo canceló o lo reasignó.' },
  missed: { title: 'Se te pasó un servicio', tone: 'warn', text: 'La oferta venció o la tomó otro conductor.' },
};

/** How the last trip ended: a strip above the state. It stays until dismissed: the driver may look a minute later. */
function ResultCard({ result, onDismiss }: { result: TripResult; onDismiss: () => void }) {
  const r = RESULT[result.kind];
  return (
    <section className={`result-card ${r.tone}`} role="status" aria-label={r.title}>
      {r.tone === 'ok' ? <CheckCircle2 size={22} aria-hidden /> : <CircleSlash size={22} aria-hidden />}
      <div className="result-main">
        <b>{r.title}</b>
        <span className="result-meta">
          <span className="code">{result.code}</span>
          {r.text && ` · ${r.text}`}
        </span>
      </div>
      <button type="button" className="btn ghost icon" onClick={onDismiss} aria-label="Cerrar aviso">
        <X size={18} aria-hidden />
      </button>
    </section>
  );
}
